import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { useTranslation } from 'react-i18next';

import { CloseIcon } from 'shared/components/icons/CloseIcon';

import { useSendPhoneVerificationCode, useVerifyPhoneNumber } from 'api/user';
import { toast } from 'shared/components/Toast/Toast';
import useUser from 'hooks/useUser';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router';
import pages from 'pages';
import { E164Number } from 'libphonenumber-js';
import { SmsModalWrapper, SmsModalHeader, SmsModalHeaderContainer } from './SmsOptInModal.styles';
import { Button } from '../../../Button/Button';
import ModalBodyType from './types';
import { LegalBody } from './LegalBody/LegalBody';
import { VerificationBody } from './VerificationBody/VerificationBody';
import { SuccessBody } from './SuccessBody/SuccessBody';

type Props = {
  handleCloseModal: () => void;
  smsModalType_StorybookOnly?: ModalBodyType;
  updateUserSettings: (variables: { user_id: string; body: { sms_opt_in: boolean } }) => void;
};

const VERIFICATION_TIME_SECONDS = 10 * 60; // 10 minutes

export function SmsOptInModal({
  handleCloseModal,
  smsModalType_StorybookOnly,
  updateUserSettings,
}: Props) {
  const theme = useTheme();

  const { t } = useTranslation();
  const [modalType, setModalType] = useState<ModalBodyType>(
    smsModalType_StorybookOnly || ModalBodyType.LEGAL,
  );

  const closeIcon = () => (
    <CloseIcon size="large" color={theme.colors.iconIconPrimarySecondary1100} />
  );
  const [timeLeft, setTimeLeft] = useState(VERIFICATION_TIME_SECONDS);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const { userResult } = useUser();
  const navigate = useNavigate();

  const user = userResult?.user;

  const { mutate: sendPhoneVerificationCode } = useSendPhoneVerificationCode({
    onSuccess: () => {
      if (modalType === ModalBodyType.LEGAL) {
        setModalType(ModalBodyType.VERIFICATION);
      } else if (modalType === ModalBodyType.VERIFICATION) {
        setTimeLeft(VERIFICATION_TIME_SECONDS);

        toast({
          type: 'success',
          title: t('toast.success'),
          message: t('account.smsOptIn.verificationModal.newCodeSent', {
            phone_number: user?.phone.phone_number,
          }),
          theme,
        });
      }
    },
    onError: ({ status }) => {
      const errorMessage =
        status === HttpStatusCode.TooManyRequests
          ? t('toast.tooManyAttempts')
          : t('toast.somethingWentWrong');
      toast({
        type: 'error',
        title: t('toast.error'),
        message: errorMessage,
        theme,
      });
    },
  });

  const { mutate: verifyPhoneNumber, isLoading: verificationLoading } = useVerifyPhoneNumber({
    onSuccess: () => {
      setModalType(ModalBodyType.SUCCESS);
      updateUserSettings({
        user_id: user?.id || '',
        body: { sms_opt_in: true },
      });
      setVerificationError(null);
    },
    onError: ({ response }) => {
      const status = response?.status;
      const unCaughtError = status !== 400 && status !== HttpStatusCode.TooManyRequests;
      if (status === 400) {
        setVerificationError(t('account.smsOptIn.verificationModal.wrongCode'));
      }
      if (status === HttpStatusCode.TooManyRequests) {
        navigate(`/${pages.TOO_MANY_ATTEMPTS}`);
      }

      if (unCaughtError) {
        toast({
          type: 'error',
          title: t('toast.error'),
          message: t('toast.somethingWentWrong'),
          theme,
        });
      }
    },
  });

  const body = useMemo(() => {
    const handleSendPhoneVerificationCode = () => {
      if (user && user.phone.phone_number) {
        sendPhoneVerificationCode({
          user_id: user.id,
          body: {
            phone_number: user.phone.phone_number as E164Number,
          },
        });
      }
    };
    const handleVerifyPin = (pin: string) => {
      setVerificationError(null);
      if (user && user.phone.phone_number) {
        verifyPhoneNumber({
          user_id: user.id,
          body: {
            verification_code: pin,
            phone_number: user.phone.phone_number as E164Number,
          },
        });
      }
    };
    switch (modalType) {
      case ModalBodyType.LEGAL:
        return (
          <LegalBody
            handleCloseModal={handleCloseModal}
            handleSendPhoneVerificationCode={handleSendPhoneVerificationCode}
            phoneNumber={user?.phone.phone_number}
          />
        );
      case ModalBodyType.VERIFICATION:
        return (
          <VerificationBody
            handleSendPhoneVerificationCode={handleSendPhoneVerificationCode}
            phoneNumber={user?.phone.phone_number}
            timeLeft={timeLeft}
            setTimeLeft={setTimeLeft}
            verificationLoading={verificationLoading}
            handleVerifyPin={handleVerifyPin}
            verificationError={verificationError}
          />
        );
      case ModalBodyType.SUCCESS:
        return <SuccessBody handleCloseModal={handleCloseModal} />;
      default:
        return null;
    }
  }, [
    modalType,
    user,
    sendPhoneVerificationCode,
    verifyPhoneNumber,
    handleCloseModal,
    timeLeft,
    verificationLoading,
    verificationError,
  ]);
  return (
    <SmsModalWrapper $modalBodyType={modalType}>
      <SmsModalHeaderContainer>
        <SmsModalHeader as="p">
          {modalType !== ModalBodyType.SUCCESS ? t('account.smsOptIn.modalHeader') : ''}
        </SmsModalHeader>
        <Button
          styleVariant="tertiary"
          Icon={closeIcon}
          onClick={handleCloseModal}
          iconRight
          margin="0 -7px 0 0"
        />
      </SmsModalHeaderContainer>

      {body}
    </SmsModalWrapper>
  );
}
export default SmsOptInModal;
